export enum DATA_EVENTS {
  /**
   * Fired after the landing screen embedding this video has been loaded.
   * Required: true
   * Endpoint: jwplayer6
   */
  setup = 'e',
  /**
   * Fired after the first frame of an ad creative has been rendered.
   * Required: true
   * Endpoint: clienta
   */
  impression = 'i',
  /**
   * Fired after the first frame of a video has been rendered.
   * Required: true
   * Endpoint: jwplayer6
   */
  play = 's',
  /**
   * Fired after the video plays past the threshold of a quantile.
   * The number of quantiles in a video depends on video length.
   * Required: true
   * Endpoint: jwplayer6
   */
  quantile = 't',
}

export interface JW_URI {
  /**
   * Analytics ID
   * All JW accounts are associated with an Analytics ID ("aid").
   * Required: true
   */
  aid: string;

  /**
   * App Version
   * Version of the App
   * Required: true
   */
  av: string;
  /**
   * App Bundle ID
   * An identifier unique to the App; persists through uninstalls.
   * Required: true
   */
  bun: string;
  /**
   * Ping Event Type
   * Event code of the ping.
   * Required: true
   */
  e: DATA_EVENTS;

  /**
   * Embed ID
   * A unique ID generated on embed for the player instance.
   * Required: true
   */
  emi: string;
  /**
   * External Player Version
   * Version of the JW Ping Specification referenced.
   * Required: false
   */
  epv: string;
  /**
   * Feed ID or Playlist ID
   * Unique ID for a feed or playlist coming out of the JW platform.
   * Required: true
   */
  fed: string;
  /**
   * Media ID
   * A unique identifier for a JW Player media item.
   * Required: true
   */
  id: string;
  /**
   * Advertising ID
   * An identifier unique to the mobile device; user can opt-out and reset.
   * Required: false
   */
  ifa: string;
  /**
   * App User ID
   * Hash of the app user account login ID. This maps to the Cleeng subscriberID.
   * Required: true
   */
  oaid: string;
  /**
   * App Install Instance ID
   * Resets with reinstalls
   * Required: false
   */
  oiid: string;
  /**
   * OS Language
   * Language of the OS on the OTT device.
   * Required: false
   */
  olng: string;
  /**
   * Device Model
   * OTT device model number.
   * Required: false
   */
  om: string;
  /**
   * Device OS
   * Name of OS on the OTT device.
   * Required: true
   */
  oos: string;
  /**
   * OS Version
   * Version of the OS on the OTT device.
   * Required: false
   */
  oosv: string;
  /**
   * Play ID
   * A unique ID generated for each playlist item successfully loaded into the player.
   * Required: true
   */
  pli: string;
  /**
   * JW Partner Player ID
   * Provided JW Partner Player ID.
   * Required: true
   */
  ppid: number;
  /**
   * Play Session Sequence
   * An counter that ticks up whenever the playlist item changes within the same embed.
   * Required: true
   */
  pss: number;
  /**
   * Ping Spec Version
   * Version of the JW Ping Specification referenced.
   * Required: false
   */
  psv: string;
  /**
   * Quantile Watched
   * The quantile that was watched based on the duration of the video.
   * Required: true
   */
  pw: number;
  /**
   * Video Quantiles
   * Number of sections in the video used for quantile tracking.
   * Required: true
   */
  q: number;
  /**
   * Timestamp (Epoch, ms)
   * The unix epoch time on client machine when ping sent.
   * Required: true
   */
  sa: number;
  /**
   * SDK Platform
   * Amount of time in seconds of video watched since the last quantile ping.
   * Required: true
   */
  sdk: string;
  /**
   * Media Title
   * The provided title for the current playlist item.
   * Required: true
   */
  t: string;
  /**
   * Time Interval (sec)
   * Amount of time in seconds of video watched since the last quantile ping.
   * Required: true
   */
  ti: number;
  /**
   * Video Duration
   * Duration of the video.
   * Required: true
   */
  vd: number;
}

export interface JW_OBJ {
  /**
   * Processes a player ready event, by invoking sendData
   * sends e events
   */
  ready: (
    aid: JW_URI['aid'],
    bun: JW_URI['bun'],
    fed: JW_URI['fed'],
    id: JW_URI['id'],
    t: JW_URI['t'],
    oosv: JW_URI['oosv'],
    av: JW_URI['av'],
    oaid: JW_URI['oaid']
  ) => void;

  /**
   * Processes an ad impression event, by invoking sendData
   * sends i events
   */
  adImpression: () => void;

  /**
   * Processes a time tick event, by invoking sendData
   * sends s events
   * sends t events
   */
  time: (vp: number, vd: JW_URI['vd']) => void;

  /**
   * Processes video complete event, by invoking sendData
   * sends t events
   */
  complete: () => void;
  seek: (offset: number, duration: number) => void;
  seeked: () => void;
  /**
   * When player is disconnected from the page -> send the rest of the data and cancel possible intervals
   */
  remove: () => void;
}
